import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { timer } from 'rxjs';
import { tap } from 'rxjs/operators';

import { Permission, SecurityScope } from '../../auth/auth.model';
import { AuthService } from '../../auth/auth.service';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { RowDto, TableDto } from '../../system-configuration/table-editor';

import {
    clearMessage,
    createRow,
    createRowCancel,
    deleteRow,
    deleteRowCancel,
    reload,
    reloadCancel,
    reloadRows,
    reloadRowsCancel,
} from './table-editor.action';
import { TranslationKeyMap } from './table-editor.i18n';
import { adminTableEditorReducer } from './table-editor.reducer';
import { TableEditorState } from './table-editor.state';

@Component({
    selector: 'cmps-admin-table-editor',
    templateUrl: './table-editor.component.html',
    host: {
        class: 'cmps-admin-table-editor',
    },
})
@TakeUntilDestroy()
@UseStore<TableEditorState>('admin.tableEditor', adminTableEditorReducer)
export class AdminTableEditorComponent
    implements OnInit, OnDestroy, StateToProps<TableEditorState>
{
    state: TableEditorState;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _form: FormGroup;
    /** @private */ _formVisible: boolean;
    /** @private */ _ref: CmpsDialogRef<CmpsConfirmDialogComponent, number>;
    _canEdit: boolean;
    _canDelete: boolean;
    _canAdd: boolean;

    constructor(
        private readonly _formBuilder: FormBuilder,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
    ) {}

    ngOnInit(): void {
        this._canEdit = this._authService.user.hasPermission(
            Permission.EDIT,
            SecurityScope.TABLE_EDITOR,
        );
        this._canDelete = this._authService.user.hasPermission(
            Permission.DELETE,
            SecurityScope.TABLE_EDITOR,
        );
        this._canAdd = this._authService.user.hasPermission(
            Permission.ADD,
            SecurityScope.TABLE_EDITOR,
        );

        useDispatcher(this, reload({}));
    }

    ngOnDestroy(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._ref && this._ref.close();
        this._ref = null;

        useDispatcher(this, reloadCancel({}));
        useDispatcher(this, reloadRowsCancel({}));
        useDispatcher(this, createRowCancel({}));
        useDispatcher(this, deleteRowCancel({}));
    }

    stateToProps(prevState?: TableEditorState): void {
        if (prevState.table !== this.state.table) {
            this._reloadForm();
        }

        if (
            prevState.pending.create &&
            !this.state.pending.create &&
            this.state.lastSaveSucceeded
        ) {
            this._form.reset();
        }

        if (prevState.message !== this.state.message && this.state.message) {
            // replace with toasts when available in uikit
            timer(5000)
                .pipe(tap(() => useDispatcher(this, clearMessage({}))))
                .subscribe();
        }
    }

    /** @private */
    _onEditTableClick(item: TableDto): void {
        useDispatcher(this, reloadRows({ tableId: item.tableId }));
    }

    /** @private */
    _onNewRowClick(): void {
        this._formVisible = true;
    }

    /** @private */
    _onCreateRowClick(): void {
        useDispatcher(this, createRow({ formValue: this._form.getRawValue() }));
    }

    /** @private */
    _onCancelCreateRowClick(): void {
        this._formVisible = false;
        this._form.reset();
    }

    /** @private */
    _onDeleteRowClick(item: RowDto): void {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._671_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_ENTRY_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        this._ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        this._ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        useDispatcher(this, deleteRow({ row: item }));
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _reloadForm(): void {
        this._form = null;
        if (this.state.table) {
            const items = {};
            this.state.table.columns.forEach((e) => {
                items[e.columnName.toLowerCase()] = [null];
            });
            this._form = this._formBuilder.group(items);
        }
    }
}
